import React, { useState, useEffect } from 'react';
import { Button, TextField, Grid, styled, Typography, Tooltip, Card, Avatar, IconButton, Box, Modal, CircularProgress, MenuItem, useTheme, Skeleton } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import WcIcon from '@mui/icons-material/Wc';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/authContext'
import { getUser, uploadProfileImageToAzure, updateUser, getUserContext, getCountries, getStates } from '../../services/apiservice';
import "cropperjs/dist/cropper.css";
import Cropper from "react-cropper";
import SnackbarComponent from '../../utils/SnackbarComponent';
import { useQueryClient } from 'react-query';
import _ from 'lodash';
import { debounce } from 'lodash';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { getAuthTokens, getSelectedRole } from '../../utils/auth';
import { useApiData } from '../../hooks/useApi';
import { linkedInUrlValidation, ProfileHeaderBand, SectionLabel, startIcon } from '../common/profile/profileFormUtils';

const RedAsterisk = styled('span')({
  color: 'red',
});

const UploadInput = styled('input')({
  display: 'none',
});

const ClickableText = styled(Typography)({
  cursor: 'pointer',
  color: 'blue',
  textDecoration: 'underline',
});

const AvatarContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
});

const EditIconButton = styled(IconButton)({
  position: 'absolute',
  bottom: 0,
  right: 0,
  backgroundColor: 'white',
  borderRadius: '50%',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  opacity: 1,
  width: 36,
  height: 36,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const UserProfile = () => {
  const queryClient = useQueryClient();
  const [profileImage, setProfileImage] = useState(null);
  const theme = useTheme();
  const [userData, setUserData] = useState({
    UserFirstName: '',
    UserLastName: '',
    UserPhoneNumber: '',
    UserGender: '',
    UserAge: '',
    UserJobTitle: '',
    UserOccupation: '',
    UserIncomeRange: '',
    UserCountry: '',
    UserState: '',
    UserCity: '',
    UserLinkedInProfileLink: '',
    UserFacebookProfileLink: '',
    UserInstagramProfileLink: '',
    UserTwitterProfileLink: '',
    UserCurrentCompanyLink: '',
    UserPastCompaniesLink: '',
    UserPhone: '',
    UserStreet1: '',
    UserStreet2: '',
    UserZip: '',
    UserCompanyEmail: '',
    UserCompanyName: '',
    UserJobIndustry: '',
    UserProfessionalExpertise: '',
    UserProfessionalSkills: '',
    UserCommunityInvolvement: '',
    UserUniversityAttended: '',
    UserGraduationYear: '',
    UserLeadershipProgramGraduatedFrom: '',
  });
  const [originalFilename, setOriginalFilename] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropData, setCropData] = useState(null);
  const [cropper, setCropper] = useState();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(userData.UserCountry || '');
  const [selectedState, setSelectedState] = useState(userData.UserState || '');
  const { user, initials, avatarColor, setUser } = useAuth();
  const selectedRole = getSelectedRole()?.selectedRole || null;
  const allowedRoles = ['Admin', 'User'];
  const email = allowedRoles.includes(user.role) ? user.UserEmail : user.IssuerRepEmailID;
  const userId = user.UserID;
  const { data: fetchedUser, loading } = useApiData(() => getUser(userId).then((res) => res.data), [userId]);
  const { data: fetchedCountries } = useApiData(() => getCountries().then((res) => res.data), []);
  const { data: fetchedStates } = useApiData(() => (selectedCountry ? getStates(selectedCountry).then((res) => res.data) : Promise.resolve([])), [selectedCountry]);

  useEffect(() => {
    if (fetchedUser) {
      setUserData(fetchedUser);
      setProfileImage(fetchedUser.ProfileImageUrl);
    }
  }, [fetchedUser]);

  useEffect(() => {
    if (fetchedCountries) {
      setCountries(fetchedCountries);
    }
  }, [fetchedCountries]);

  useEffect(() => {
    if (fetchedStates) {
      setStates(fetchedStates);
    }
  }, [fetchedStates]);

  useEffect(() => {
    setSelectedCountry(userData.UserCountry || '');
    setSelectedState(userData.UserState || '');
  }, [userData]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const imageUrl = reader.result;
        setSelectedImage(imageUrl);
        setOriginalFilename(file.name);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarClick = () => {
    setIsModalOpen(true);
  };

  const handleUpload = async () => {
    if (typeof cropper !== "undefined") {
      const croppedData = cropper.getCroppedCanvas().toDataURL();
      const file = dataURLtoFile(croppedData, 'cropped-image.png', originalFilename);
      setCropData(file);
    }
  };

  const dataURLtoFile = (dataurl, filename, originalFilename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], originalFilename, { type: mime });
  };

  const handleClose = () => {
    setCropModalOpen(false);
    setFileInputKey((prevKey) => prevKey + 1);
    setSelectedImage(null);
    setCropper(null);
    setCropData(null);
  };
  useEffect(() => {
    const uploadCroppedImage = async () => {
      if (!cropData) return;
      setCropModalOpen(false);
      setUploading(true);
      try {
        const updatedImageUrl = await uploadProfileImageToAzure(cropData, userId, user.role);

        const img = new Image();
        img.src = updatedImageUrl;

        img.onload = () => {
          queryClient.invalidateQueries('profileImage');
          setFileInputKey((prevKey) => prevKey + 1);
        };
        const userContextResponse = await getUserContext(email, selectedRole);
        if (userContextResponse.success) {
          setUser({ ...userContextResponse.data.userContext, role: selectedRole });
          setSnackbarSeverity('success');
          setSnackbarMessage('Profile image updated successfully');
          setProfileImage(updatedImageUrl);
        } else {
          setSnackbarSeverity('error');
          setSnackbarMessage('Error fetching updated user context');
        }

      } catch (error) {
        setSnackbarSeverity('error');
        setSnackbarMessage('Error updating profile image');
      } finally {
        setUploading(false);
        setSnackbarOpen(true);
        setCropper(null);
        setCropData(null);
      }
    };

    uploadCroppedImage();
  }, [cropData, user, setUser]);


  const validationSchema = Yup.object({
    UserFirstName: Yup.string()
      .required('First Name is required')
      .matches(/^[A-Za-z\s]+$/, 'First Name must contain only alphabets'),

    UserLastName: Yup.string()
      .required('Last Name is required')
      .matches(/^[A-Za-z\s]+$/, 'Last Name must contain only alphabets'),

    UserPhoneNumber: Yup.string()
      .required('Contact Number is required'),

    // UserAge: Yup.number()
    //   .required('Age is required')
    //   .min(1, 'Age must be a positive number')
    //   .max(120, 'Age must be less than or equal to 120'), 

    UserGender: Yup.string()
      .required('Gender is required')
      .oneOf(['Male', 'Female', 'Other'], 'Gender must be either Male, Female, or Other'),

    UserJobTitle: Yup.string()
      .required('Job Title is required')
      .matches(/^[A-Za-z\s]+$/, 'Job Title must contain only alphabets'),

    UserLinkedInProfileLink: linkedInUrlValidation,
  });


  const handleDebouncedChange = debounce((event, handleChange) => {
    handleChange(event);
  }, 100);

  const MemoizedField = React.memo(({ as, ...props }) => {
    return (
      <Field
        as={as}
        {...props}
      />
    );
  });

  return (
    <>
      <Helmet>
        <title>Settings</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Formik
        initialValues={userData || {}}
        validationSchema={validationSchema}
        enableReinitialize={Boolean(userData)}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const updatedFields = Object.keys(values).reduce((acc, key) => {
              // if (values[key] !== userData[key]) {
              //   acc[key] = values[key];
              // }
              // return acc;
              let value = values[key];

              // Convert empty strings to null for all fields
              if (value === "") {
                value = null;
              }

              if (value !== userData[key]) {
                acc[key] = value;
              }
              return acc;
            }, {});

            const hasChanges = Object.keys(updatedFields).some(key => updatedFields[key] !== userData[key]);

            if (!hasChanges) {
              setSnackbarSeverity('warning');
              setSnackbarMessage('Please update before submitting');
              setSnackbarOpen(true);
              return;
            }

            const response = await updateUser({ ...updatedFields, UserID: userId });

            if (response.success) {
              const userContextResponse = await getUserContext(email, selectedRole);
              if (userContextResponse.success) {
                setUser({ ...userContextResponse.data.userContext, role: selectedRole });
                setSnackbarSeverity('success');
                setSnackbarMessage('Profile Updated successfully!');
              } else {
                setSnackbarSeverity('error');
                setSnackbarMessage('Error fetching updated user context');
              }

            } else {
              setSnackbarMessage('Failed to Update Profile.');
              setSnackbarSeverity('error');
            }
            setUserData({ ...userData, ...updatedFields });
            setSnackbarOpen(true);
          } catch (error) {
            console.error('Submission error:', error);
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          } finally {
            setSubmitting(false);
          }


        }}
      >
        {({ errors, touched, isSubmitting, values, handleChange, setFieldValue }) => (
          <Card sx={{ width: 'calc(100% - 40px)', maxWidth: 980, mx: 'auto', my: 3, borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
            <ProfileHeaderBand>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
                User Profile
              </Typography>
              <Grid container justifyContent="center">
                <Grid item>
                  <Box sx={{ position: 'relative' }}>
                  {loading ? (
                    <Skeleton variant="circular" width={120} height={120} />
                  ) : (
                    <AvatarContainer>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={profileImage || undefined}
                          sx={{
                            width: 120,
                            height: 120,
                            opacity: uploading ? 0.5 : 1,
                            filter: uploading ? 'blur(2px)' : 'none',
                            backgroundColor: avatarColor,
                            display: profileImage ? 'block' : 'flex',
                            justifyContent: profileImage ? 'initial' : 'center',
                            alignItems: profileImage ? 'initial' : 'center',
                            fontSize: '3rem',
                            border: '3px solid #fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                          }}
                          onClick={handleAvatarClick}
                        >
                          {!profileImage && initials}
                        </Avatar>
                        {uploading && (
                          <CircularProgress
                            size={40}
                            sx={{
                              position: 'absolute',
                              top: '35%',
                              left: '35%',
                              transform: 'translate(-35%, -35%)',
                              zIndex: 1,
                              color: 'white'
                            }}
                          />
                        )}
                        <EditIconButton
                          className="edit-icon"
                          onClick={() => document.getElementById('profile-image-input')?.click()}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            zIndex: 1,
                          }}
                        >
                          <CameraAltIcon />
                        </EditIconButton>
                      </Box>
                      <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.92)' }}>
                        {user.UserEmail}
                      </Typography>
                      <input
                        type="file"
                        id="profile-image-input"
                        key={fileInputKey}
                        onChange={handleProfileImageChange}
                        style={{ display: 'none' }}
                      />
                      {cropModalOpen && (
                        <Modal
                          open={cropModalOpen}
                          onClose={handleClose}
                          aria-labelledby="modal-title"
                          aria-describedby="modal-description"
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{
                              width: '400px',
                              height: '400px',
                              border: '1px solid #ddd',
                              borderRadius: '10px',
                              boxShadow: '0px 0px 10px rgba(0,0,0,0.2)',
                              display: 'flex',
                              marginLeft: 30,
                              justifyContent: 'center',
                              alignItems: 'center',
                              overflow: 'hidden'
                            }}>
                              <Cropper
                                zoomTo={0.5}
                                initialAspectRatio={1}
                                preview=".img-preview"
                                viewMode={1}
                                minCropBoxHeight={10}
                                minCropBoxWidth={10}
                                background={false}
                                responsive={true}
                                autoCropArea={1}
                                checkOrientation={false}
                                src={selectedImage}
                                onInitialized={(instance) => {
                                  setCropper(instance);
                                }}
                                style={{
                                  maxHeight: '100%',
                                  maxWidth: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '20px', }}>
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ mr: 2 }}
                                onClick={() => {
                                  handleClose();
                                }}
                              >
                                {'Cancel'}
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ ml: 14 }}
                                onClick={handleUpload}
                              >
                                {'Upload'}
                              </Button>
                            </Box>
                          </Box>
                        </Modal>
                      )}
                      {isModalOpen && (
                        <Modal
                          open={isModalOpen}
                          onClose={() => setIsModalOpen(false)}
                          aria-labelledby="modal-title"
                          aria-describedby="modal-description"
                        >
                          <Box sx={{
                            position: 'absolute',
                            top: '48%',
                            left: '62.5%',
                            transform: 'translate(-70%, -70%)',
                            p: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1,
                            [theme.breakpoints.down('sm')]: {
                              top: '40%',
                              left: '58%',
                              transform: 'translate(-60%, -45%)',
                              width: '90vw',
                              height: '80vh',
                            },
                          }}>
                            <img src={profileImage} alt="Profile Picture" style={{
                              maxWidth: '100%',
                              maxHeight: '100vh',
                              objectFit: 'cover',
                              borderRadius: '10px',
                              width: '300px',
                              height: '300px',
                              [theme.breakpoints.down('sm')]: {
                                width: '100%',
                                height: '100%',
                              },
                            }} />
                            <IconButton onClick={() => setIsModalOpen(false)} sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              color: 'white',
                              [theme.breakpoints.down('sm')]: {
                                top: 5,
                                right: 5,
                              },
                            }}>
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Modal>
                      )}
                    </AvatarContainer>
                  )}
                </Box>
              </Grid>
            </Grid>
            </ProfileHeaderBand>

            <Box sx={{ p: 3 }}>
            <Form>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <SectionLabel>Personal</SectionLabel>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'First Name'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="UserFirstName"
                      fullWidth
                      InputProps={startIcon(<PersonIcon fontSize="small" />)}
                      error={touched.UserFirstName && Boolean(errors.UserFirstName)}
                      helperText={touched.UserFirstName && errors.UserFirstName}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Last Name'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="UserLastName"
                      fullWidth
                      InputProps={startIcon(<PersonIcon fontSize="small" />)}
                      error={touched.UserLastName && Boolean(errors.UserLastName)}
                      helperText={touched.UserLastName && errors.UserLastName}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Contact Number'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="UserPhoneNumber"
                      fullWidth
                      InputProps={startIcon(<PhoneIcon fontSize="small" />)}
                      error={touched.UserPhoneNumber && Boolean(errors.UserPhoneNumber)}
                      helperText={touched.UserPhoneNumber && errors.UserPhoneNumber}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Gender'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="UserGender"
                      fullWidth
                      InputProps={startIcon(<WcIcon fontSize="small" />)}
                      error={touched.UserGender && Boolean(errors.UserGender)}
                      helperText={touched.UserGender && errors.UserGender}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Tooltip
                      title="You're a student! Please update your job title to 'Student'."
                      arrow
                    >
                      <Field
                        as={TextField}
                        label={
                          <>
                            {'Job Title'} <RedAsterisk>*</RedAsterisk>
                          </>
                        }
                        name="UserJobTitle"
                        fullWidth
                        InputProps={startIcon(<WorkIcon fontSize="small" />)}
                        error={touched.UserJobTitle && Boolean(errors.UserJobTitle)}
                        helperText={touched.UserJobTitle && errors.UserJobTitle}
                      />
                    </Tooltip>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <SectionLabel>Company</SectionLabel>
                </Grid>
                {/* User Company Name */}
                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label='Company Name'
                      name="UserCompanyName"
                      fullWidth
                      InputProps={startIcon(<BusinessIcon fontSize="small" />)}
                      error={touched.UserCompanyName && Boolean(errors.UserCompanyName)}
                      helperText={touched.UserCompanyName && errors.UserCompanyName}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <SectionLabel>Social</SectionLabel>
                </Grid>
                {/* User LinkedIn Profile URL (required) */}
                <Grid item xs={12}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'LinkedIn Profile URL'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="UserLinkedInProfileLink"
                      fullWidth
                      placeholder="https://www.linkedin.com/in/your-name"
                      InputProps={startIcon(<LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />)}
                      error={touched.UserLinkedInProfileLink && Boolean(errors.UserLinkedInProfileLink)}
                      helperText={touched.UserLinkedInProfileLink && errors.UserLinkedInProfileLink}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <ClickableText
                    onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                  >
                    {showAdditionalDetails ? 'Hide More Details' : 'Add More Details'}
                  </ClickableText>
                </Grid>

                {showAdditionalDetails && (
                  <>
                    {/* User age */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label='Age'
                          name="UserAge"
                          fullWidth
                          error={touched.UserAge && Boolean(errors.UserAge)}
                          helperText={touched.UserAge && errors.UserAge}
                        />
                      )}
                    </Grid>
                    {/* User Occupation */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label='Occupation'
                          name="UserOccupation"
                          fullWidth
                          error={touched.UserOccupation && Boolean(errors.UserOccupation)}
                          helperText={touched.UserOccupation && errors.UserOccupation}
                        />
                      )}
                    </Grid>

                    {/* User Income Range */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label='Income Range'
                          name="UserIncomeRange"
                          fullWidth
                          error={touched.UserIncomeRange && Boolean(errors.UserIncomeRange)}
                          helperText={touched.UserIncomeRange && errors.UserIncomeRange}
                        />
                      )}
                    </Grid>

                    {/* User Country */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label="Country"
                          name="UserCountry"
                          fullWidth
                          select
                          value={values.UserCountry}
                          onChange={(e) => {
                            setSelectedCountry(e.target.value);
                            setSelectedState('');
                            setFieldValue('UserState', '');
                            handleChange(e);
                          }}
                          error={touched.UserCountry && Boolean(errors.UserCountry)}
                          helperText={touched.UserCountry && errors.UserCountry}
                          SelectProps={{
                            MenuProps: {
                              disablePortal: true,
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  overflowY: 'auto',
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Country</em>
                          </MenuItem>
                          {countries.map((country) => (
                            <MenuItem key={country.isoCode} value={country.isoCode}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </MemoizedField>
                      )}
                    </Grid>

                    {/* User State */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label="State"
                          name="UserState"
                          fullWidth
                          select
                          value={values.UserState || ""}
                          disabled={!selectedCountry}
                          onChange={(e) => {
                            setSelectedState(e.target.value);
                            handleChange(e);
                          }}
                          error={touched.UserState && Boolean(errors.UserState)}
                          helperText={touched.UserState && errors.UserState}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                style: { maxHeight: 200, overflowY: 'auto' },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select State</em>
                          </MenuItem>
                          {selectedCountry && states.length > 0 ? (
                            states.map((state) => (
                              <MenuItem key={state.isoCode} value={state.isoCode}>
                                {state.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              No states available
                            </MenuItem>
                          )}
                        </MemoizedField>
                      )}
                    </Grid>


                    {/* User City */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label='City'
                          name="UserCity"
                          fullWidth
                          error={touched.UserCity && Boolean(errors.UserCity)}
                          helperText={touched.UserCity && errors.UserCity}
                        />
                      )}
                    </Grid>

                    {/* User Facebook Profile Link */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <MemoizedField
                          as={TextField}
                          label='Facebook Profile Link'
                          name="UserFacebookProfileLink"
                          fullWidth
                          error={touched.UserFacebookProfileLink && Boolean(errors.UserFacebookProfileLink)}
                          helperText={touched.UserFacebookProfileLink && errors.UserFacebookProfileLink}
                        />
                      )}
                    </Grid>
                    {/* User Instagram Profile Link */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Instagram Profile Link'
                          name="UserInstagramProfileLink"
                          fullWidth
                          error={touched.UserInstagramProfileLink && Boolean(errors.UserInstagramProfileLink)}
                          helperText={touched.UserInstagramProfileLink && errors.UserInstagramProfileLink}
                        />
                      )}
                    </Grid>

                    {/* User Twitter Profile Link */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Twitter Profile Link'
                          name="UserTwitterProfileLink"
                          fullWidth
                          error={touched.UserTwitterProfileLink && Boolean(errors.UserTwitterProfileLink)}
                          helperText={touched.UserTwitterProfileLink && errors.UserTwitterProfileLink}
                        />
                      )}
                    </Grid>

                    {/* User Current Company Link */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Current Company Link'
                          name="UserCurrentCompanyLink"
                          fullWidth
                          error={touched.UserCurrentCompanyLink && Boolean(errors.UserCurrentCompanyLink)}
                          helperText={touched.UserCurrentCompanyLink && errors.UserCurrentCompanyLink}
                        />
                      )}
                    </Grid>

                    {/* User Past Companies Link */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Past Companies Link'
                          name="UserPastCompaniesLink"
                          fullWidth
                          error={touched.UserPastCompaniesLink && Boolean(errors.UserPastCompaniesLink)}
                          helperText={touched.UserPastCompaniesLink && errors.UserPastCompaniesLink}
                        />
                      )}
                    </Grid>

                    {/* User Phone */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Phone'
                          name="UserPhone"
                          fullWidth
                          error={touched.UserPhone && Boolean(errors.UserPhone)}
                          helperText={touched.UserPhone && errors.UserPhone}
                        />
                      )}
                    </Grid>

                    {/* User Street 1 */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Street Address 1'
                          name="UserStreet1"
                          fullWidth
                          error={touched.UserStreet1 && Boolean(errors.UserStreet1)}
                          helperText={touched.UserStreet1 && errors.UserStreet1}
                        />
                      )}
                    </Grid>

                    {/* User Street 2 */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Street Address 2'
                          name="UserStreet2"
                          fullWidth
                          error={touched.UserStreet2 && Boolean(errors.UserStreet2)}
                          helperText={touched.UserStreet2 && errors.UserStreet2}
                        />
                      )}
                    </Grid>

                    {/* User Zip */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Zip Code'
                          name="UserZip"
                          fullWidth
                          error={touched.UserZip && Boolean(errors.UserZip)}
                          helperText={touched.UserZip && errors.UserZip}
                        />
                      )}
                    </Grid>

                    {/* User Company Email */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Company Email'
                          name="UserCompanyEmail"
                          fullWidth
                          error={touched.UserCompanyEmail && Boolean(errors.UserCompanyEmail)}
                          helperText={touched.UserCompanyEmail && errors.UserCompanyEmail}
                        />
                      )}
                    </Grid>

                    {/* User Job Industry */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Job Industry'
                          name="UserJobIndustry"
                          fullWidth
                          error={touched.UserJobIndustry && Boolean(errors.UserJobIndustry)}
                          helperText={touched.UserJobIndustry && errors.UserJobIndustry}
                        />
                      )}
                    </Grid>

                    {/* User Professional Expertise */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Professional Expertise'
                          name="UserProfessionalExpertise"
                          fullWidth
                          error={touched.UserProfessionalExpertise && Boolean(errors.UserProfessionalExpertise)}
                          helperText={touched.UserProfessionalExpertise && errors.UserProfessionalExpertise}
                        />
                      )}
                    </Grid>

                    {/* User Professional Skills */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Professional Skills'
                          name="UserProfessionalSkills"
                          fullWidth
                          error={touched.UserProfessionalSkills && Boolean(errors.UserProfessionalSkills)}
                          helperText={touched.UserProfessionalSkills && errors.UserProfessionalSkills}
                        />
                      )}
                    </Grid>

                    {/* User Community Involvement */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Community Involvement'
                          name="UserCommunityInvolvement"
                          fullWidth
                          error={touched.UserCommunityInvolvement && Boolean(errors.UserCommunityInvolvement)}
                          helperText={touched.UserCommunityInvolvement && errors.UserCommunityInvolvement}
                        />
                      )}
                    </Grid>

                    {/* User University Attended */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='University Attended'
                          name="UserUniversityAttended"
                          fullWidth
                          error={touched.UserUniversityAttended && Boolean(errors.UserUniversityAttended)}
                          helperText={touched.UserUniversityAttended && errors.UserUniversityAttended}
                        />
                      )}
                    </Grid>

                    {/* User Graduation Year */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Graduation Year'
                          name="UserGraduationYear"
                          fullWidth
                          error={touched.UserGraduationYear && Boolean(errors.UserGraduationYear)}
                          helperText={touched.UserGraduationYear && errors.UserGraduationYear}
                        />
                      )}
                    </Grid>

                    {/* User Leadership Program Graduated From */}
                    <Grid item xs={12} sm={6}>
                      {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Field
                          as={TextField}
                          label='Leadership Program Graduated From'
                          name="UserLeadershipProgramGraduatedFrom"
                          fullWidth
                          error={touched.UserLeadershipProgramGraduatedFrom && Boolean(errors.UserLeadershipProgramGraduatedFrom)}
                          helperText={touched.UserLeadershipProgramGraduatedFrom && errors.UserLeadershipProgramGraduatedFrom}
                        />
                      )}
                    </Grid>

                  </>
                )}
                <Grid item xs={12} display="flex" justifyContent="center">
                  {loading ? (
                    <Skeleton variant="rectangular" width={90} height={42} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting && <CircularProgress size={24} color="inherit" />}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  )}
                </Grid>
              </Grid>

            </Form>
            </Box>
          </Card>
        )}
      </Formik>
    </>
  );
};

export default UserProfile;